import { StatusBadge } from "@/components/status-badge";
import { cn } from "@/lib/utils";

type JobStatus =
  | "pending"
  | "processing"
  | "ready_for_review"
  | "completed"
  | "failed";

interface JobStatusBadgeProps {
  status: JobStatus;
  className?: string;
}

/* Theme-aware status colors using CSS custom properties */
const statusConfig: Record<
  JobStatus,
  { label: string; variant: string; showSpinner?: boolean }
> = {
  pending: {
    label: "Pending",
    variant: "status-pending",
    showSpinner: true,
  },
  processing: {
    label: "Processing",
    variant: "status-processing",
    showSpinner: true,
  },
  ready_for_review: {
    label: "Ready for Review",
    variant: "status-review",
  },
  completed: {
    label: "Completed",
    variant: "status-completed",
  },
  failed: {
    label: "Failed",
    variant: "status-error",
  },
};

const variantStyles: Record<string, string> = {
  "status-pending": "bg-status-pending/15 text-status-pending border-status-pending/40 border-2",
  "status-processing": "bg-status-processing/15 text-status-processing border-status-processing/40 border-2",
  "status-review": "bg-status-review/15 text-status-review border-status-review/40 border-2",
  "status-completed": "bg-status-completed/15 text-status-completed border-status-completed/40 border-2",
  "status-error": "bg-status-error/15 text-status-error border-status-error/40 border-2",
};

export function JobStatusBadge({ status, className }: JobStatusBadgeProps) {
  const config = statusConfig[status];

  return (
    <StatusBadge
      label={config.label}
      variant="outline"
      showSpinner={config.showSpinner}
      className={cn(
        "font-bold text-xs px-3 py-1.5 uppercase tracking-wider transition-colors",
        variantStyles[config.variant],
        className
      )}
    />
  );
}

/* 
 * Usage Example:
 *
 * import { JobStatusBadge } from "@/components/job-status-badge";
 *
 * function JobRow({ job }) {
 *   return (
 *     <TableRow>
 *       <TableCell>{job.name}</TableCell>
 *       <TableCell>
 *         <JobStatusBadge status={job.status} />
 *       </TableCell>
 *     </TableRow>
 *   );
 * }
 *
 * Accessibility:
 * - Uses semantic Badge component
 * - Spinner has aria-hidden for screen readers
 * - Color choices maintain sufficient contrast ratios
 * - Theme-aware colors adapt to selected theme
 * - Bold text and borders for visibility
 */
